import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useParams, RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
    LoadingIndicator,
    MarkdownDisplay,
    StepWizard,
    triggerSurvey,
} from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, Card } from '@ygtechnologies/rubus-ui';

import { IState } from '@/state/IState';
import { useOptionsScope } from '../OptionsScope';
import ConfirmIdentity from '@/buyer/components/ConfirmIdentity/ConfirmIdentity';
import { useIsMobile, useAsyncState, useAppDispatch } from '@/utils/hooks';
import { getUserSettings } from '@/services/users';
import { requestOneTimeCode } from '@/services/signatures';
import { classnames } from '@/utils/html';
import { HttpStatusCode } from '@/HttpStatusCodes';
import { loadProjectData } from '@/state/buyer/project-data/creator';
import {
    ConfiguratorSignatureDto,
    configuratorKeys,
    prepareConfiguratorSignature,
    signConfigurator,
} from '@/services/configurator';

import theme from './SubmitWizard.module.scss';
import { ConfirmUpgrades } from './ConfirmUpgrades';
import { PreSignCheck } from './PreSignCheck';

enum SubmitError {
    Dependencies,
}

interface RouteParams {
    projectDeadlineId: string;
}

type Props = RouteComponentProps<RouteParams> & {
    backToOptionsUrl: string;
};

async function describeError(e: unknown): Promise<string> {
    if (e instanceof Response) {
        try {
            const body = await e.clone().text();
            return body ? `${e.status} ${e.statusText}: ${body}` : `${e.status} ${e.statusText}`;
        } catch {
            return `${e.status} ${e.statusText}`;
        }
    }
    if (e instanceof Error) return e.message;
    return String(e);
}

export const BuyerOptionSubmit: React.FC<Props> = ({ backToOptionsUrl }) => {
    const isMobile = useIsMobile();
    const { projectDeadlineId } = useParams<RouteParams>();
    const { projectData, residentialUnitId } = useOptionsScope();
    const activeProfile = useSelector((state: IState) => state.global.activeProfile);
    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();

    const profileId = activeProfile?.id;
    const deadline = projectData?.deadlines.find((dl) => dl.id === parseInt(projectDeadlineId, 10));
    const deadlineId = deadline?.id ?? null;

    const [preCleared, setPreCleared] = useState(false);
    const [code, setCode] = useState('');
    const [sent, setSent] = useState(false);
    const [oneTimeCodeError, setOneTimeCodeError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [signatureInfo, setSignatureInfo] = useState<ConfiguratorSignatureDto | null>(null);
    const [error, setError] = useState<SubmitError | null>(null);
    const [fatalError, setFatalError] = useState<string | null>(null);

    const [userSettings] = useAsyncState(() => getUserSettings(), [], null);

    useEffect(() => {
        let cancelled = false;
        async function loadData() {
            // Don't prepare a signature until the buyer has resolved any unselected options.
            if (!preCleared) return;
            if (!deadlineId || !residentialUnitId || profileId == null) {
                setSignatureInfo(null);
                return;
            }
            try {
                const info = await prepareConfiguratorSignature(residentialUnitId, deadlineId, profileId);
                if (!cancelled) {
                    setSignatureInfo(info);
                    setError(null);
                    setFatalError(null);
                }
            } catch (e: unknown) {
                if ((e as { status?: number })?.status === HttpStatusCode.Conflict) {
                    if (!cancelled) setError(SubmitError.Dependencies);
                } else {
                    const msg = await describeError(e);
                    console.error('prepareConfiguratorSignature failed', e);
                    if (!cancelled) setFatalError(msg);
                }
            }
        }
        void loadData();
        return () => {
            cancelled = true;
        };
    }, [residentialUnitId, deadlineId, profileId, preCleared]);

    const handleSend = async () => {
        if (!signatureInfo || profileId == null) return false;
        setOneTimeCodeError(false);
        setIsLoading(true);
        try {
            await signConfigurator(signatureInfo.clientKey, code, profileId);
            setSent(true);
            // After signing, the deadline's awaitingSignatures and each group's
            // isLocked are stale until we refetch. Invalidate the configurator
            // query and reload projectData so the options page (and sidebar
            // unsigned counter) reflect the signed state when the buyer
            // navigates back.
            if (residentialUnitId != null) {
                await queryClient.invalidateQueries({
                    queryKey: configuratorKeys.byUnit(residentialUnitId),
                });
            }
            dispatch(loadProjectData(residentialUnitId));
            try {
                triggerSurvey();
            } catch {
                // survey is best-effort
            }
            return true;
        } catch (e: unknown) {
            if ((e as { status?: number })?.status === HttpStatusCode.BadRequest) {
                setOneTimeCodeError(true);
            } else {
                const msg = await describeError(e);
                console.error('signConfigurator failed', e);
                setFatalError(msg);
            }
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    if (!projectData || activeProfile == null) {
        return <LoadingIndicator />;
    }

    if (error === SubmitError.Dependencies) {
        return (
            <div>
                <AlertBanner type="danger">
                    <Trans>Some selections are incompatible and must be fixed before signing</Trans>
                </AlertBanner>
                <Button as={Link} to={backToOptionsUrl} size="lg" block variant="tertiary">
                    <Trans>Back to options</Trans>
                </Button>
            </div>
        );
    }

    if (fatalError) {
        return (
            <div className={theme.container}>
                <AlertBanner type="danger">
                    <Trans>Sign and send failed</Trans>: {fatalError}
                </AlertBanner>
                <Button as={Link} to={backToOptionsUrl} size="lg" block variant="tertiary">
                    <Trans>Back to options</Trans>
                </Button>
            </div>
        );
    }

    if (!deadline) {
        return (
            <div className={theme.container}>
                <AlertBanner type="warning">
                    <Trans>Deadline not found (looked for id {projectDeadlineId}). Make sure the link is correct.</Trans>
                </AlertBanner>
                <Button as={Link} to={backToOptionsUrl} size="lg" block variant="tertiary">
                    <Trans>Back to options</Trans>
                </Button>
            </div>
        );
    }

    if (!preCleared) {
        return (
            <div className={theme.container}>
                <PreSignCheck
                    deadlineId={deadline.id}
                    backToOptionsUrl={backToOptionsUrl}
                    onCleared={() => setPreCleared(true)}
                />
            </div>
        );
    }

    if (!signatureInfo) {
        return <LoadingIndicator />;
    }

    return (
        <div className={theme.container}>
            {!isMobile && <h2>{t`Sign and send options for ${deadline.name}`}</h2>}
            {isMobile && (
                <>
                    <h2>
                        <Trans>Options</Trans>
                    </h2>
                    <h6>
                        <Trans>Sign and send options for {deadline.name}</Trans>
                    </h6>
                </>
            )}
            <div className={theme.wizardContainer}>
                <StepWizard>
                    {projectData.optionsTerms ? (
                        <StepWizard.Step
                            hideBack
                            buttonClassName={theme.buttons}
                            nextText={<Trans>I accept</Trans>}
                            title={<Trans>Terms and conditions</Trans>}
                            buttonSize="xl">
                            <Card className={theme.termsCard}>
                                <MarkdownDisplay markdown={projectData.optionsTerms} />
                            </Card>
                        </StepWizard.Step>
                    ) : null}

                    <StepWizard.Step
                        hideBack
                        disableNext={userSettings !== null && !userSettings.phoneNumber}
                        buttonClassName={theme.buttons}
                        nextText={<Trans>Confirm options and continue to signing</Trans>}
                        onNextCallBack={() => requestOneTimeCode(signatureInfo.clientKey)}
                        title={<Trans>Confirm options</Trans>}
                        buttonSize="xl">
                        <AlertBanner type="info">
                            <Trans>
                                This is where you are ordering and signing the options for your home. After you complete this
                                wizard you will not be able to change the choices you submit.
                            </Trans>
                        </AlertBanner>
                        <ConfirmUpgrades signatureInfo={signatureInfo} />
                    </StepWizard.Step>

                    <StepWizard.Step
                        disableNext={code.length !== 5 || isLoading}
                        nextText={<Trans>Click here to sign</Trans>}
                        buttonClassName={theme.buttons}
                        hideBack
                        nextPreCheck={() => handleSend().then((r) => r)}
                        title={<Trans>Confirm Identity</Trans>}
                        buttonSize="xl">
                        <ConfirmIdentity
                            hasError={oneTimeCodeError}
                            code={code}
                            onCodeChange={setCode}
                            onResendClick={() => requestOneTimeCode(signatureInfo.clientKey)}
                            message={
                                <Trans>
                                    Please enter the five digit code that we have sent to your phone to confirm your identity
                                    as the buyer of {projectData.unitName} at {projectData.projectName}. After signing this
                                    order you will not be able to make changes to the choices you have made on all items with
                                    deadline {deadline.name}.
                                </Trans>
                            }
                        />
                    </StepWizard.Step>
                    <StepWizard.Step hideBack title={<Trans>Thank you</Trans>}>
                        <Card className={theme.thankYouCard}>
                            <h5>
                                <Trans>Thank you for your order</Trans>
                            </h5>
                            <p>
                                <Trans>
                                    Your order has been placed. You can always see the products you ordered on the budget
                                    page.
                                </Trans>
                            </p>
                        </Card>
                    </StepWizard.Step>
                </StepWizard>
                <div className={classnames(theme.buttons, theme.signButtonContainer)}>
                    <Button as={Link} to={backToOptionsUrl} variant="primary" size="xl" disabled={!sent}>
                        <Trans>Back to options</Trans>
                    </Button>
                </div>
            </div>
        </div>
    );
};
